import React from "react";
import Seo from "../components/Seo";
import { Hero, Section, SectionLabel, Band } from "../components/Section";
import { Numbered } from "../components/Table";
import NominationForm from "../components/NominationForm";
import { Compass, Scale, Presentation, UsersRound, Network, Globe2, FileCheck2, Waypoints } from "lucide-react";
import { EditorialIcon, ArchitectureIllustration } from "../components/EditorialVisuals";

const LEAD =
  "Lead.ai equips executives and boards to lead AI adoption in their organisations: the leadership to drive it and the policy to govern it.";

const PROGRAMMES = [
  {
    title: "Executive Programme on AI Adoption Leadership",
    body: "For the executives who will drive adoption: what to prioritise, what to invest in, how to build alignment, when to move. Applied learning on the participant's own organisation, not theory.",
  },
  {
    title: "Executive Programme on Enterprise AI Adoption Policy",
    body: "For the executives and board members who will govern adoption: the policy, controls and accountability an organisation needs before it scales.",
  },
];

const ENGAGE = [{ title: "Master classes." }, { title: "Industry round tables." }, { title: "Working groups." }, { title: "Benchmarking missions across Aspen countries." }];

const LEAVE_WITH = [
  { title: "Executive decision-making confidence on matters of AI adoption." },
  { title: "A governance position for their own organisation, written during the programme." },
  { title: "A peer network of senior leaders across sectors and Aspen countries." },
];

export default function LeadAi() {
  return (
    <>
      <Seo title="Lead.ai — Syni.ai" description={LEAD} path="/lead-ai" />
      <Hero title="Lead.ai" lead={LEAD} testId="leadai-hero" />

      <Section n="01" label="Why executives, not pilots" split pale testId="leadai-why" visual={<ArchitectureIllustration kind="leadership" testId="leadai-leadership-illustration" />}>
        <div className="prose body-copy measure">
          <p>
            AI must be owned by the executives and the board, not left to pilots. Scattered experiments multiply exposure faster than anyone can
            track. What is needed is clear goals, a clear why, and clear integration of AI skills into the operating model.
          </p>
          <p>
            Don't treat AI the way you treat software. Treat its adoption the way you would treat the recruitment of a high-value resource into
            your organisation: vetted, given a mandate, held to account. You are not afraid to recruit an accomplished PhD, because you know you
            have the structures to direct, contain and get value from that person. AI is the same. Lead.ai builds those structures at the top of
            the organisation.
          </p>
        </div>
      </Section>

      <Section n="02" label="Two programmes" testId="leadai-programmes">
        <div className="grid-cards grid-cards--2" data-testid="leadai-programme-cards">
          {PROGRAMMES.map((p, i) => (
            <div key={p.title} className="card" data-testid={`leadai-programme-${i + 1}`}>
              <EditorialIcon icon={i === 0 ? Compass : Scale} className="programme-icon" testId={`leadai-programme-icon-${i + 1}`} />
              <h3 className="card-title">{p.title}</h3>
              <p className="card-body">{p.body}</p>
              <div className="programme-meta">
                <span className="mono-label">Details on request</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section n="03" label="The Fellowship" title="The Aspen + Syni.ai Executive Fellowship on AI Adoption and Governance" split pale testId="leadai-fellowship">
        <div className="prose body-copy measure" style={{ marginBottom: 32 }}>
          <p>
            A cohort of senior leaders working through adoption and governance decisions in their own organisations, convened with Aspen
            Initiative Nairobi. Fellows engage through:
          </p>
        </div>
        <Numbered items={ENGAGE} icons={[Presentation, UsersRound, Network, Globe2]} plain testId="leadai-engage" />
        <p className="body-copy measure" style={{ marginTop: 32 }} data-testid="leadai-fellowship-opens">
          The Fellowship opens in November 2026 with a working breakfast session. Participation is by nomination.
        </p>
      </Section>

      <Section n="04" label="What participants leave with" split testId="leadai-leave">
        <Numbered items={LEAVE_WITH} icons={[Compass, FileCheck2, Waypoints]} plain testId="leadai-leave-list" />
      </Section>

      <Band label="Nominate an executive" title="Participation is by nomination." testId="leadai-band" />

      <section className="section" id="nominate" data-testid="leadai-form-section">
        <div className="container-syni">
          <div className="section-head">
            <SectionLabel>Form</SectionLabel>
            <h2 className="h-section">Nominate an executive</h2>
          </div>
          <div className="form-wrap">
            <NominationForm />
          </div>
        </div>
      </section>
    </>
  );
}
